import axiosInstance from "./Axios";

const API_BASE = "/social-accounts";

export type FacebookPage = {
  page_id: string;
  page_name: string;
  category?: string | null;
  picture?: string | null;
  page_access_token: string;
};

export type InstagramAccountOption = {
  page_id: string;
  page_name?: string | null;
  instagram_business_account_id: string;
  username: string;
  picture?: string | null;
  page_access_token: string;
};

export type GoogleBusinessLocationOption = {
  google_account_id: string;
  google_account_name?: string | null;
  google_location_id: string;
  google_location_name: string;
  address?: any | null;
};

export type GoogleBusinessLocationDetail = {
  google_location_id: string;
  google_location_name: string;
  verified: boolean;
  address?: any | null;
  websiteUri?: string | null;
  primaryPhone?: string | null;
  image?: string | null;
  category?: string | null;
};

export type LinkedInOrganizationOption = {
  organization_id: string;
  organization_name: string;
  organization_logo?: string | null;
  organization_urn?: string;
};

export class SocialMediaApi {
  static async getFacebookConnectUrl() {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(`${API_BASE}/facebook/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data?.auth_url;
    } catch (err: any) {
      console.error("Error getting Facebook connect URL:", err);
      throw err;
    }
  }

  static async getFacebookPages(tokenParam: string) {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.get(`${API_BASE}/facebook/pages`, {
        params: { fb_token: tokenParam },
        headers: { Authorization: `Bearer ${token}` },
      });

      return res.data?.data?.pages || [];
    } catch (err: any) {
      console.error("Error fetching Facebook pages:", err);
      throw err;
    }
  }

  static async getFacebookPageDetail(pageId: string) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(
        `${API_BASE}/facebook/page-detail/${pageId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return res.data?.data;
    } catch (err: any) {
      console.error("Error fetching Facebook page detail:", err);
      throw err;
    }
  }

  static async selectFacebookPage(payload: FacebookPage) {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.post(
        `${API_BASE}/facebook/select-page`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return res.data;
    } catch (err: any) {
      console.error("Error selecting Facebook page:", err);
      throw err;
    }
  }

  static async getInstagramConnectUrl() {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(`${API_BASE}/instagram/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data?.auth_url;
    } catch (err: any) {
      console.error("Error getting Instagram connect URL:", err);
      throw err;
    }
  }

  static async getInstagramAccountDetail(igId: string) {
    const token = localStorage.getItem("token");
    const res = await axiosInstance.get(
      `${API_BASE}/instagram/account-detail/${igId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data?.data;
  }

  static async getInstagramAccounts() {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(`${API_BASE}/instagram/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data?.accounts || [];
    } catch (err: any) {
      console.error("Error fetching Instagram accounts:", err);
      throw err;
    }
  }

  static async checkFacebookPermissions(payload: {
    page_id: string;
    page_access_token: string;
  }) {
    const token = localStorage.getItem("token");
    const res = await axiosInstance.post(
      `${API_BASE}/facebook/check-permissions`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data?.data;
  }

  static async checkInstagramPermissions(payload: {
    page_id: string;
    instagram_business_account_id: string;
    page_access_token: string;
  }) {
    const token = localStorage.getItem("token");
    const res = await axiosInstance.post(
      `${API_BASE}/instagram/check-permissions`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data?.data;
  }

  static async checkLinkedInPermissions(pageId?: string) {
    const token = localStorage.getItem("token");
    const res = await axiosInstance.get(
      `${API_BASE}/linkedin/check-permissions`,
      {
        params: pageId ? { pageId } : undefined,
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data?.data;
  }

  static async selectInstagramAccount(payload: InstagramAccountOption) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.post(
        `${API_BASE}/instagram/select-account`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return res.data;
    } catch (err: any) {
      console.error("Error selecting Instagram account:", err);
      throw err;
    }
  }

  static async getYouTubeConnectUrl() {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(`${API_BASE}/youtube/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data?.auth_url;
    } catch (err: any) {
      console.error("Error getting YouTube connect URL:", err);
      throw err;
    }
  }

  static async getTikTokConnectUrl() {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(`${API_BASE}/tiktok/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data?.auth_url;
    } catch (err: any) {
      console.error("Error getting TikTok connect URL:", err);
      throw err;
    }
  }

  static async getGoogleBusinessConnectUrl() {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.get(
        `${API_BASE}/google-business/connect`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return res.data?.data?.auth_url;
    } catch (err: any) {
      console.error("Error getting Google Business connect URL:", err);
      throw err;
    }
  }

  static async getGoogleBusinessVerifiedLocations() {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(
        `${API_BASE}/google-business/locations/verified`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return res.data?.data?.locations || [];
    } catch (err: any) {
      console.error("Error fetching verified Google Business locations:", err);
      throw err;
    }
  }

  static async getGoogleBusinessLocations() {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.get(
        `${API_BASE}/google-business/locations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return Array.isArray(res.data?.data?.locations)
        ? res.data.data.locations
        : [];
    } catch (err: any) {
      console.error("Error fetching Google Business locations:", err);
      throw err;
    }
  }

  static async selectGoogleBusinessLocation(
    payload: GoogleBusinessLocationOption,
  ) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.post(
        `${API_BASE}/google-business/select-location`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return res.data;
    } catch (err: any) {
      console.error("Error selecting Google Business location:", err);
      throw err;
    }
  }

  static async getGoogleBusinessLocationDetail(socialAccountId: string) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(
        `${API_BASE}/google-business/location/${socialAccountId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return res.data?.data as GoogleBusinessLocationDetail;
    } catch (err: any) {
      console.error("Error fetching Google Business location detail:", err);
      throw err;
    }
  }

  static async getLinkedInOrganizations() {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(
        `${API_BASE}/linkedin/organizations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data?.data?.pages || [];
    } catch (err: any) {
      console.error("Error fetching LinkedIn organizations:", err);
      throw err;
    }
  }

  static async selectLinkedInOrganization(payload: LinkedInOrganizationOption) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.post(
        `${API_BASE}/linkedin/select-organization`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return res.data;
    } catch (err: any) {
      console.error("Error selecting LinkedIn organization:", err);
      throw err;
    }
  }

  static async checkLinkedInConnection() {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.get(
        `${API_BASE}/linkedin/check-connection`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return res.data?.data;
    } catch (err: any) {
      console.error("Error checking LinkedIn connection:", err);
      throw err;
    }
  }

  static async refreshLinkedInRoles(pageId: string) {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.get(
        `${API_BASE}/linkedin/refresh-roles/${pageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return res.data?.data;
    } catch (err: any) {
      console.error("Error refreshing LinkedIn roles:", err);
      throw err;
    }
  }

  static async getAllConnectedAccounts() {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const CALL_TIMEOUT = 8000;

  const withPlatform = (platform: string, arr: any[]) =>
    (Array.isArray(arr) ? arr : []).map((a: any) => ({ ...a, platform }));

  const results = await Promise.allSettled([
    axiosInstance.get(`${API_BASE}/facebook/all-accounts`, {
      headers,
      timeout: CALL_TIMEOUT,
    }),
    axiosInstance.get(`${API_BASE}/instagram/all-accounts`, {
      headers,
      timeout: CALL_TIMEOUT,
    }),
    axiosInstance.get(`${API_BASE}/linkedin/all-accounts`, {
      headers,
      timeout: CALL_TIMEOUT,
    }),
    axiosInstance.get(`${API_BASE}/google-business/all-accounts`, {
      headers,
      timeout: CALL_TIMEOUT,
    }),
  ]);

  const safeAccounts = (
    result: PromiseSettledResult<any>,
    platform: string,
  ) => {
    if (result.status === "fulfilled") {
      return withPlatform(platform, result.value?.data?.data?.accounts);
    }
    console.warn(`[${platform}] accounts fetch failed:`, result.reason);
    return [];
  };

  return {
    data: {
      accounts: [
        ...safeAccounts(results[0], "facebook"),
        ...safeAccounts(results[1], "instagram"),
        ...safeAccounts(results[2], "linkedin"),
        ...safeAccounts(results[3], "google_business"),
      ],
    },
  };
}

  // static async getAllConnectedAccounts() {
  //   const token = localStorage.getItem("token");

  //   const withPlatform = (platform: string, arr: any[]) =>
  //     (Array.isArray(arr) ? arr : []).map((a: any) => ({ ...a, platform }));

  //   try {
  //     const [
  //       facebookRes,
  //       instagramRes,
  //       youtubeRes,
  //       tiktokRes,
  //       linkedinRes,
  //       googleBusinessRes,
  //     ] = await Promise.all([
  //       axiosInstance.get(`${API_BASE}/facebook/all-accounts`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }),
  //       axiosInstance.get(`${API_BASE}/instagram/all-accounts`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }),
  //       axiosInstance.get(`${API_BASE}/youtube/all-accounts`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }),
  //       axiosInstance.get(`${API_BASE}/tiktok/all-accounts`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }),
  //       axiosInstance.get(`${API_BASE}/linkedin/all-accounts`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }),
  //       axiosInstance.get(`${API_BASE}/google-business/all-accounts`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }),
  //     ]);

  //     const facebookAccounts = withPlatform(
  //       "facebook",
  //       facebookRes.data?.data?.accounts,
  //     );

  //     const instagramAccounts = withPlatform(
  //       "instagram",
  //       instagramRes.data?.data?.accounts,
  //     );

  //     const youtubeAccounts = withPlatform(
  //       "youtube",
  //       youtubeRes.data?.data?.accounts,
  //     );

  //     const tiktokAccounts = withPlatform(
  //       "tiktok",
  //       tiktokRes.data?.data?.accounts,
  //     );

  //     const linkedinAccounts = withPlatform(
  //       "linkedin",
  //       linkedinRes.data?.data?.accounts,
  //     );

  //     const googleBusinessAccounts = withPlatform(
  //       "google_business",
  //       googleBusinessRes.data?.data?.accounts,
  //     );

  //     return {
  //       data: {
  //         accounts: [
  //           ...facebookAccounts,
  //           ...instagramAccounts,
  //           ...youtubeAccounts,
  //           ...tiktokAccounts,
  //           ...linkedinAccounts,
  //           ...googleBusinessAccounts,
  //         ],
  //       },
  //     };
  //   } catch (err: any) {
  //     console.error("Error fetching combined accounts:", err);
  //     throw err;
  //   }
  // }

  static async getAllConnectedInstagramAccounts() {
    const token = localStorage.getItem("token");
    const res = await axiosInstance.get(`${API_BASE}/instagram/all-accounts`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  }

  static async getAllConnectedYouTubeAccounts() {
    const token = localStorage.getItem("token");
    const res = await axiosInstance.get(`${API_BASE}/youtube/all-accounts`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  }

  static async getAllConnectedTikTokAccounts() {
    const token = localStorage.getItem("token");
    const res = await axiosInstance.get(`${API_BASE}/tiktok/all-accounts`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  }

  static async removeAccount(id: string) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.delete(
        `${API_BASE}/facebook/remove-account/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err: any) {
      console.error("Error removing Facebook account:", err);
      throw err;
    }
  }

  static async removeLinkedInAccount(id: string) {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.delete(
        `${API_BASE}/linkedin/remove-account/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return res.data;
    } catch (err: any) {
      console.error("Error removing LinkedIn account:", err);
      throw err;
    }
  }

  static async removeInstagramAccount(id: string) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.delete(
        `${API_BASE}/instagram/remove-account/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err: any) {
      console.error("Error removing Instagram account:", err);
      throw err;
    }
  }

  static async removeYouTubeAccount(id: string) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.delete(
        `${API_BASE}/youtube/remove-account/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err: any) {
      console.error("Error removing YouTube account:", err);
      throw err;
    }
  }

  static async removeTikTokAccount(id: string) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.delete(
        `${API_BASE}/tiktok/remove-account/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err: any) {
      console.error("Error removing TikTok account:", err);
      throw err;
    }
  }

  static async removeGoogleBusinessAccount(id: string) {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.delete(
        `${API_BASE}/google-business/remove-account/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return res.data;
    } catch (err: any) {
      console.error("Error removing Google Business account:", err);
      throw err;
    }
  }

  static async postFacebookImage(params: {
    imageUrl?: string;
    caption?: string;
    scheduleType: "post_now" | "schedule_datetime" | "auto_post";
    scheduledTime?: string;
    postId?: string;
  }) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.post(
        `${API_BASE}/facebook/post-image`,
        params,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err: any) {
      console.error("Error posting image to Facebook:", err);
      throw err;
    }
  }

  static async postInstagramImage(params: {
    imageUrl?: string;
    caption?: string;
    scheduleType: "post_now" | "schedule_datetime" | "auto_post";
    scheduledTime?: string;
  }) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.post(
        `${API_BASE}/instagram/post-image`,
        params,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err: any) {
      console.error("Erfror posting image to Instagram:", err);
      throw err;
    }
  }

  static async postYouTubeVideo(params: {
    title: string;
    description?: string;
    tags?: string[];
    videoUrl: string;
  }) {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.post(
        `${API_BASE}/youtube/post-video`,
        params,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err: any) {
      console.error("Error posting video to YouTube:", err);
      throw err;
    }
  }

  static async connect(
    platform:
      | "facebook"
      | "instagram"
      | "linkedin"
      | "youtube"
      | "google_business"
      | "tiktok",
  ) {
    try {
      const endpoint =
        platform === "google_business" ? "google-business" : platform;

      const res = await axiosInstance.get(`${API_BASE}/${endpoint}/connect`);

      return res.data?.data?.auth_url;
    } catch (err: any) {
      console.error(`Error connecting to ${platform}:`, err);
      throw err;
    }
  }

  static async getAll(
    platform: "facebook" | "instagram" | "linkedin" | "youtube",
  ) {
    try {
      const res = await axiosInstance.get(
        `${API_BASE}/${platform}/all-accounts`,
      );
      return res.data?.data?.accounts || [];
    } catch (err: any) {
      console.error(`Error fetching ${platform} accounts:`, err);
      return [];
    }
  }
}
